import React, {
  FC,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react'
import { Column } from 'react-table'
import { css } from '@emotion/react'
import { Flex } from '@chakra-ui/react'
import { t, Trans } from '@lingui/macro'

import {
  CampaignBannerModelAsEditor,
  CampaignCertificationShareActionModelAsEditor,
  CampaignEmailActionModelAsEditor,
  CampaignMessageActionModelAsEditor,
  CampaignPathwayShareActionModelAsEditor,
  CampaignSurveyShareActionModelAsEditor,
  CampaignTopicShareActionModelAsEditor,
  CampaignUserReportRow,
  NotificationQueueProcessedModel,
  ScheduledActionReport,
  UserModelAsAdminQueries
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import IconPeople from 'front-end/src/components/common/icon/IconPeople'
import { formatDateAndTimeShort } from 'front-end/src/utils/dateHelpers'
import TableExtendedOld from 'front-end/src/components/common/table-extended-old/TableExtendedOld'
import IconDoc from '../../../components/common/icon/IconDoc'

import { ManageCampaignContext, SingleCampaign } from '../ManageCampaignPage'
import { createActionsTimeline } from '../campaign-actions/CampaignActions'
import TablePagination from '../../../components/common/table-extended-old/table-extended-pagination/TablePagination'
import UserDetail from '../../../components/common/user/user-detail/UserDetail'
import { CampaignUserEventTypeEnum } from '../../../../../shared/enums/campaigns'
import Tag from '../../../components/common/tag/Tag'
import { ICampaignActionReportFilters } from './CampaignActionReport'
import ResettableInput from '../../../components/common/form/input/ResettableInput'
import { useDebouncedState } from '../../../app/hooks/useDebouncedState'
import Tooltip from '../../../components/common/tooltip/Tooltip'
import { NotificationProcessedStateEnum } from '../../../../../shared/enums/NotificationQueueProcessed'
import CampaignActionReportDownloadCSV from './CampaignActionReportDownloadCSV'
import {
  isCampaignFixed,
  isCampaignImmediate,
  isCampaignRelative
} from '../utils'
import Loader from '../../../components/common/loader/Loader'
import IconEnvelope from '../../../components/common/icon/IconEnvelope'
import IconBubble from '../../../components/common/icon/IconBubble'
import IconBubbles from '../../../components/common/icon/IconBubbles'
import IconScreenDesktop from '../../../components/common/icon/IconScreenDesktop'
import IconMap from '../../../components/common/icon/IconMap'
import IconBadge from '../../../components/common/icon/IconBadge'
import { useCampaignReportsQuery } from './CampaignActionReport.codegen'
import { campaignMessageTypeLabel } from '../common'
import dayjs from 'shared/dayjs'

// TODO break up this big file into several components

// Naming based on table in this document https://docs.google.com/document/d/1M7bmWc5qu4evgNSJ5ckiOQGwRkwe9g-ZlfqRUN_mWzU/edit#
export enum ReportTypeEnum {
  ACTIVITY = 'activity',
  ENGAGEMENT = 'engagement',
  SUCCESS = 'success',
  SCHEDULED = 'scheduled',
  MISSING = 'missing',
  FAILED = 'failed'
}

interface IEnrolmentsEvent {
  sent: string
  started: string
  completed: string
}

interface IMessageEvent {
  sent: string
}

interface IEmailEvent {
  sent: string
  opened: string
  clicked: string
}

interface IBannerEvent {
  shown: string
  clicked: string
}

export interface IScheduledAction {
  banner: CampaignBannerModelAsEditor | null
  email: CampaignEmailActionModelAsEditor | null
  topicShareAction: CampaignTopicShareActionModelAsEditor | null
  pathwayShareAction: CampaignPathwayShareActionModelAsEditor | null
  surveyShareAction: CampaignSurveyShareActionModelAsEditor | null
  message: CampaignMessageActionModelAsEditor | null
  certificationShareAction: CampaignCertificationShareActionModelAsEditor | null
}

interface IReportProps {
  onFilterChange: (filters: Partial<ICampaignActionReportFilters>) => void
  filters: ICampaignActionReportFilters
}

const CampaignActionReportOverviewTable: FC<IReportProps> = ({
  onFilterChange,
  filters
}) => {
  const limit = 20
  const { campaign } = useContext(ManageCampaignContext)
  const { data, loading } = useCampaignReportsQuery({
    variables: {
      campaignId: campaign.id,
      filter: {
        displayName: filters.search.length > 0 ? filters.search : null,
        page: filters.page,
        limit,
        group_ids: filters.groupIds.length > 0 ? filters.groupIds : null
      }
    }
  })
  const report = data?.admin?.campaign?.report
  const [search, setSearch] = useState<string>(filters.search || '')
  const debouncedSearchTerm = useDebouncedState(search, 250)

  useEffect(() => {
    onFilterChange({ search })
  }, [debouncedSearchTerm])

  const renderHeaderActionColumn = (
    day: string,
    scheduledAction: IScheduledAction
  ) => {
    let actionName = ''
    let actionInfo = ''
    if (scheduledAction.banner) {
      actionName = t`Show banner`
      actionInfo = scheduledAction.banner.header
    }

    if (scheduledAction.email) {
      if (scheduledAction.email.is_for_manager) {
        actionName = t`Notify manager`
      } else {
        actionName = t`Send email`
      }
      actionInfo = scheduledAction.email.subject
    }

    if (scheduledAction.topicShareAction) {
      actionName = t`Share resource`
      const topicsCount = scheduledAction.topicShareAction.actionTopics.length
      actionInfo =
        topicsCount > 1
          ? t`${topicsCount} resources`
          : scheduledAction.topicShareAction.actionTopics[0].topic.currentTitle
    }

    if (scheduledAction.pathwayShareAction) {
      actionName = t`Share pathway`
      const pathwaysCount =
        scheduledAction.pathwayShareAction.actionPathways.length
      actionInfo =
        pathwaysCount > 1
          ? t`${pathwaysCount} pathways`
          : scheduledAction.pathwayShareAction.actionPathways[0].pathway.title
    }

    if (scheduledAction.surveyShareAction) {
      actionName = t`Share survey`
      const surveysCount =
        scheduledAction.surveyShareAction.actionSurveys.length
      actionInfo =
        surveysCount > 1
          ? t`${surveysCount} surveys`
          : scheduledAction.surveyShareAction.actionSurveys[0].survey.title
    }

    if (scheduledAction.message) {
      actionName = t`Send ${campaignMessageTypeLabel(
        scheduledAction.message.type!
      )} message`
      actionInfo = scheduledAction.message.text ?? ''
    }

    if (scheduledAction.certificationShareAction) {
      actionName = t`Share certification`
      const certificationsCount =
        scheduledAction.certificationShareAction.actionCertifications.length
      actionInfo =
        certificationsCount > 1
          ? t`${certificationsCount} certifications`
          : scheduledAction.certificationShareAction.actionCertifications[0]
              .certification.name
    }

    return (
      <header className="EventHeader">
        {(isCampaignFixed(campaign) || isCampaignRelative(campaign)) &&
          renderDayLabel(day, campaign)}
        {isCampaignImmediate(campaign) &&
          renderImmediateActionLabel(scheduledAction)}
        <div className="EventHeader__name">
          <div>
            <strong>{actionName}</strong>
          </div>
          <div className="EventHeader__recordName">{actionInfo}</div>
        </div>
      </header>
    )
  }

  const renderDayLabel = (day: string, campaign: SingleCampaign) => {
    if (isCampaignFixed(campaign)) {
      const formattedTimestamp = dayjs(day, 'YYYY MM DD')
      const formattedMonth = formattedTimestamp.format('MMM')
      const formattedDay = formattedTimestamp.format('D')
      const formattedYear = formattedTimestamp.format('YYYY')
      return (
        <div className="EventHeader__label EventHeader__label--fixed">
          <span className="EventHeader__label__top">{formattedMonth}</span>
          <span className="EventHeader__label__middle">{formattedDay}</span>
          <span className="EventHeader__label__bottom">{formattedYear}</span>
        </div>
      )
    } else if (isCampaignRelative(campaign)) {
      return (
        <div className="EventHeader__label EventHeader__label--relative">
          <Trans>DAY</Trans>{' '}
          <div className="EventHeader__label__number">{day}</div>
        </div>
      )
    }
  }

  const createColumnsFromTimeline = (timeline): Array<Column<any>> => {
    const tableColumns: Array<Column<any>> = []
    Object.keys(timeline).forEach((day) => {
      Object.keys(timeline[day])
        .sort()
        .forEach((hour) => {
          timeline[day][hour].forEach((scheduledAction) => {
            // Not rendering bannerHide action in timeline.
            // We just want to render banner show action and render user activity in that cell.
            // BannerHide would show same cell data as BannerShow column.
            if (!scheduledAction.bannerHide) {
              tableColumns.push({
                Header: renderHeaderActionColumn(day, scheduledAction),
                sortable: false,
                width: 72,
                className: 'UserEvent',
                Cell: (row) => {
                  return renderScheduledActionReport(
                    scheduledAction,
                    row.original as Partial<CampaignUserReportRow>
                  )
                }
              })
            }
          })
        })
    })
    return tableColumns
  }

  let timeline = createActionsTimeline(campaign)

  if (timeline['null']) {
    // In campaign action report, we don't want to show actions with incorrect
    // schedule setup. These are shown only in the campaign actions list.
    // Copied action does not have schedule set up yet, so it does not make sense to show it.
    delete timeline['null']
  }

  const sortedTimeline = {}
  Object.keys(timeline)
    .sort((a, b) => {
      if (isCampaignFixed(campaign)) {
        return b < a ? 1 : -1
      } else if (isCampaignRelative(campaign)) {
        return parseInt(b as any, 10) < parseInt(a as any, 10) ? 1 : -1
      } else {
        throw Error(`Cannot sort campaign of type ${campaign.schedule_type}`)
      }
    })
    .forEach((day) => (sortedTimeline[day] = timeline[day]))

  const timelineColumns = createColumnsFromTimeline(sortedTimeline)
  const tableColumns: Array<Column<any>> = [
    {
      Header: (
        <ResettableInput
          onChange={setSearch}
          placeholder={t`Search`}
          title={t`Search`}
          showResetButton={!!search}
          showSearchIcon
          value={search}
          onReset={() => {
            setSearch('')
            onFilterChange({ search: '' })
          }}
        />
      ),
      width: 200,
      sortable: false,
      headerClassName: 'FixedColumn FixedColumn__EventUserName',
      className: 'FixedColumn FixedColumn__EventUserName',
      accessor: 'user.display_name',
      Cell: (row) => {
        return <UserDetail {...row.original.user} />
      }
    },
    {
      Header: t`First joined`,
      width: 160,
      id: 'joinDate',
      sortable: false,
      headerClassName: 'FixedColumn FixedColumn__EventJoinDate',
      className: 'FixedColumn FixedColumn__EventJoinDate',
      accessor: 'firstJoinDate',
      Cell: (row) => {
        if (!row.value) {
          return '-'
        }
        return formatDateAndTimeShort(row.value)
      }
    },
    {
      Header: t`Status`,
      accessor: 'lastCampaignEvent',
      headerClassName: 'FixedColumn FixedColumn__EventStatus',
      className: 'FixedColumn FixedColumn__EventStatus',
      sortable: false,
      Cell: (row) => {
        if (!row.original.lastCampaignEvent) {
          return '-'
        }
        if (row.original.lastCampaignEvent === CampaignUserEventTypeEnum.join) {
          return (
            <Tag color="blue">
              <Trans>Active</Trans>
            </Tag>
          )
        }

        if (
          row.original.lastCampaignEvent === CampaignUserEventTypeEnum.leave
        ) {
          return (
            <Tag color="grey">
              <Trans>Removed</Trans>
            </Tag>
          )
        }

        return (
          <Tag color="green">
            <Trans>Completed</Trans>
          </Tag>
        )
      }
    },
    ...timelineColumns
  ]

  return (
    <TableExtendedOld
      manual
      alwaysShowHeader
      minRows={1}
      columns={tableColumns}
      resizable={false}
      loading={loading}
      loadingText={
        <>
          <Loader /> <Trans>Loading...</Trans>
        </>
      }
      page={filters.page}
      pages={report ? Math.ceil((report.usersCount || 1) / limit) : 1}
      onPageChange={(page) => {
        onFilterChange({ page: page })
      }}
      PaginationComponent={(props) => (
        <TablePagination
          {...props}
          paginationContentAfter={
            <CampaignActionReportDownloadCSV
              filters={filters}
              itemsCount={report?.usersCount || 0}
              sortedTimeline={sortedTimeline}
            />
          }
        />
      )}
      data={report ? report.users : []}
    />
  )
}

const renderScheduledActionReport = (
  scheduledActionToRender: IScheduledAction,
  userReportRow: CampaignUserReportRow
) => {
  const allUserScheduledActions =
    userReportRow.scheduledActions as ScheduledActionReport[]
  let userActionReport
  if (scheduledActionToRender.banner) {
    userActionReport = allUserScheduledActions.find(
      (scheduledAction) =>
        scheduledAction.campaign_banner_id ===
        scheduledActionToRender.banner?.id
    )
    if (userActionReport && userActionReport.bannerEvent) {
      return renderBannerReport(userActionReport)
    }
  }

  if (scheduledActionToRender.email) {
    userActionReport = allUserScheduledActions.find(
      (scheduledAction) =>
        scheduledAction.campaign_email_action_id ===
        scheduledActionToRender.email?.id
    )
    if (userActionReport && userActionReport.emailEvent) {
      return renderEmailReport(
        userActionReport,
        userReportRow.user as UserModelAsAdminQueries,
        scheduledActionToRender
      )
    }
  }

  if (scheduledActionToRender.topicShareAction) {
    userActionReport = allUserScheduledActions.find(
      (scheduledAction) =>
        scheduledAction.campaign_topic_share_action_id ===
        scheduledActionToRender.topicShareAction?.id
    )
    if (userActionReport && userActionReport.topicEnrolmentsEvent) {
      return renderEnrolmentReport(
        userActionReport,
        userActionReport.topicEnrolmentsEvent
      )
    }
  }

  if (scheduledActionToRender.pathwayShareAction) {
    userActionReport = allUserScheduledActions.find(
      (scheduledAction) =>
        scheduledAction.campaign_pathway_share_action_id ===
        scheduledActionToRender.pathwayShareAction?.id
    )
    if (userActionReport && userActionReport.pathwayEnrolmentsEvent) {
      return renderEnrolmentReport(
        userActionReport,
        userActionReport.pathwayEnrolmentsEvent
      )
    }
  }

  if (scheduledActionToRender.certificationShareAction) {
    userActionReport = allUserScheduledActions.find(
      (scheduledAction) =>
        scheduledAction.campaign_certification_share_action_id ===
        scheduledActionToRender.certificationShareAction?.id
    )
    if (userActionReport && userActionReport.certificationEnrolmentsEvent) {
      return renderEnrolmentReport(
        userActionReport,
        userActionReport.certificationEnrolmentsEvent
      )
    }
  }

  if (scheduledActionToRender.surveyShareAction) {
    userActionReport = allUserScheduledActions.find(
      (scheduledAction) =>
        scheduledAction.campaign_survey_share_action_id ===
        scheduledActionToRender.surveyShareAction?.id
    )
    if (userActionReport && userActionReport.surveysEvent) {
      return renderEnrolmentReport(
        userActionReport,
        userActionReport.surveysEvent
      )
    }
  }

  if (scheduledActionToRender.message) {
    userActionReport = allUserScheduledActions.find(
      (scheduledAction) =>
        scheduledAction.campaign_message_action_id ===
        scheduledActionToRender.message?.id
    )
    if (userActionReport && userActionReport.messageEvent) {
      return renderMessageReport(userActionReport)
    }
  }

  return renderReportTooltip(
    t`This action is not scheduled for this user. It's possible that this user was added into this campaign after this action had taken place.`,
    ReportTypeEnum.MISSING
  )
}

const renderReportTooltip = (html: ReactElement | string, type: string) => {
  return (
    <Tooltip
      className={`CampaignReports__event CampaignReports__event--${type}`}
      text={html}
    >
      &nbsp;
    </Tooltip>
  )
}

const reportClassNameForType = (reportType: ReportTypeEnum) => {
  return `EventTooltip__type EventTooltip__type--${reportType}`
}

const renderBannerReport = (scheduledAction: ScheduledActionReport) => {
  const bannerEvent = scheduledAction.bannerEvent as IBannerEvent

  let type = ReportTypeEnum.SCHEDULED
  if (bannerEvent.shown) {
    type = ReportTypeEnum.ENGAGEMENT
  }
  if (bannerEvent.clicked) {
    type = ReportTypeEnum.SUCCESS
  }

  const html = (
    <>
      <div className="EventTooltip">
        <span className={reportClassNameForType(ReportTypeEnum.SCHEDULED)} />
        <Trans>Scheduled</Trans>:{' '}
        {formatDateAndTimeShort(scheduledAction.scheduled_time)}
      </div>
      <div className="EventTooltip">
        <span className={reportClassNameForType(ReportTypeEnum.ENGAGEMENT)} />
        <Trans>First shown</Trans>: {formatDateAndTimeShort(bannerEvent.shown)}
      </div>
      <div className="EventTooltip">
        <span className={reportClassNameForType(ReportTypeEnum.SUCCESS)} />
        <Trans>First clicked</Trans>:{' '}
        {formatDateAndTimeShort(bannerEvent.clicked)}
      </div>
    </>
  )
  return renderReportTooltip(html, type)
}

const renderEmailReport = (
  scheduledActionUserReport: ScheduledActionReport,
  user: UserModelAsAdminQueries,
  scheduledAction: IScheduledAction
) => {
  const { scheduled_time, processedNotification } = scheduledActionUserReport
  const emailEvent = scheduledActionUserReport.emailEvent as IEmailEvent

  let html: ReactElement
  let type = ReportTypeEnum.SCHEDULED

  if (emailEvent.sent) {
    type = ReportTypeEnum.ACTIVITY
  }
  if (emailEvent.opened) {
    type = ReportTypeEnum.ENGAGEMENT
  }
  if (emailEvent.clicked) {
    type = ReportTypeEnum.SUCCESS
  }
  if (processedNotification && hasNotificationError(processedNotification)) {
    html = renderSendErrorReport(scheduledActionUserReport, emailEvent.sent)
    type = ReportTypeEnum.FAILED
  } else {
    html = (
      <>
        {scheduledAction.email?.is_for_manager &&
          (user.manager ? (
            <>
              <Flex mb={2}>
                <Trans>For manager</Trans>:{' '}
              </Flex>
              <UserDetail
                display_name={user.manager.display_name}
                imageUrl={user.manager.image_url}
                css={css`
                  .UserDetail__name {
                    color: #fff;
                  }
                `}
              />
            </>
          ) : (
            t`${
              user.display_name || user.email || user.account_name
            } has no manager. Action will be skipped.`
          ))}
        {(scheduledAction.email?.is_for_manager === false || user.manager) && (
          <>
            <div className="EventTooltip">
              <span
                className={reportClassNameForType(ReportTypeEnum.SCHEDULED)}
              />
              <Trans>Scheduled</Trans>: {formatDateAndTimeShort(scheduled_time)}
            </div>
            <div className="EventTooltip">
              <span
                className={reportClassNameForType(ReportTypeEnum.ACTIVITY)}
              />
              <Trans>Sent</Trans>: {formatDateAndTimeShort(emailEvent.sent)}
            </div>
            <div className="EventTooltip">
              <span
                className={reportClassNameForType(ReportTypeEnum.ENGAGEMENT)}
              />
              <Trans>Opened</Trans>: {formatDateAndTimeShort(emailEvent.opened)}
            </div>
            <div className="EventTooltip">
              <span
                className={reportClassNameForType(ReportTypeEnum.SUCCESS)}
              />
              <Trans>Clicked</Trans>:{' '}
              {formatDateAndTimeShort(emailEvent.clicked)}
            </div>
          </>
        )}
      </>
    )
  }

  return renderReportTooltip(html, type)
}

const renderMessageReport = (scheduledAction: ScheduledActionReport) => {
  const { scheduled_time, processedNotification } = scheduledAction
  const messageEvent = scheduledAction.messageEvent as IMessageEvent
  let html: ReactElement
  let type = ReportTypeEnum.SCHEDULED

  if (messageEvent.sent) {
    type = ReportTypeEnum.SUCCESS
  }

  if (processedNotification && hasNotificationError(processedNotification)) {
    html = renderSendErrorReport(scheduledAction, messageEvent.sent)
    type = ReportTypeEnum.FAILED
  } else {
    html = (
      <>
        <div className="EventTooltip">
          <span className={reportClassNameForType(ReportTypeEnum.SCHEDULED)} />
          <Trans>Scheduled</Trans>: {formatDateAndTimeShort(scheduled_time)}
        </div>
        <div className="EventTooltip">
          <span className={reportClassNameForType(ReportTypeEnum.SUCCESS)} />
          <Trans>Completed</Trans>: {formatDateAndTimeShort(messageEvent.sent)}
        </div>
      </>
    )
  }

  return renderReportTooltip(html, type)
}

const renderEnrolmentReport = (
  scheduledAction: ScheduledActionReport,
  enrolmentEvent: IEnrolmentsEvent
) => {
  const { scheduled_time, processedNotification } = scheduledAction

  let html: ReactElement
  let type = ReportTypeEnum.SCHEDULED
  if (enrolmentEvent.sent) {
    type = ReportTypeEnum.ACTIVITY
  }
  if (enrolmentEvent.started) {
    type = ReportTypeEnum.ENGAGEMENT
  }
  if (enrolmentEvent.completed) {
    type = ReportTypeEnum.SUCCESS
  }
  if (processedNotification && hasNotificationError(processedNotification)) {
    html = renderSendErrorReport(scheduledAction, enrolmentEvent.sent)
    type = ReportTypeEnum.FAILED
  } else {
    html = (
      <>
        <div className="EventTooltip">
          <span className={reportClassNameForType(ReportTypeEnum.SCHEDULED)} />
          <Trans>Scheduled</Trans>: {formatDateAndTimeShort(scheduled_time)}
        </div>
        <div className="EventTooltip">
          <span className={reportClassNameForType(ReportTypeEnum.ACTIVITY)} />
          <Trans>Sent/Shared</Trans>:{' '}
          {formatDateAndTimeShort(enrolmentEvent.sent)}
        </div>
        <div className="EventTooltip">
          <span className={reportClassNameForType(ReportTypeEnum.ENGAGEMENT)} />
          <Trans>Started</Trans>:{' '}
          {formatDateAndTimeShort(enrolmentEvent.started)}
        </div>
        <div className="EventTooltip">
          <span className={reportClassNameForType(ReportTypeEnum.SUCCESS)} />
          <Trans>Completed</Trans>:{' '}
          {formatDateAndTimeShort(enrolmentEvent.completed)}
        </div>
      </>
    )
  }

  return renderReportTooltip(html, type)
}

const renderSendErrorReport = (
  scheduledAction: ScheduledActionReport,
  sentTime: string
) => {
  return (
    <>
      <div className="EventTooltip">
        <span className={reportClassNameForType(ReportTypeEnum.SCHEDULED)} />
        <Trans>Scheduled</Trans>:{' '}
        {formatDateAndTimeShort(scheduledAction.scheduled_time)}
      </div>
      <div className="EventTooltip">
        <span className={reportClassNameForType(ReportTypeEnum.FAILED)} />
        <Trans>Failed to send</Trans>: {formatDateAndTimeShort(sentTime)}
      </div>
      <div className="EventTooltip__error">
        {getErrorReason(
          scheduledAction.processedNotification as NotificationQueueProcessedModel
        )}
      </div>
    </>
  )
}

export const hasNotificationError = (
  processedNotification: NotificationQueueProcessedModel
) => {
  return ![
    NotificationProcessedStateEnum.PROCESSED,
    NotificationProcessedStateEnum.DELIVERY
  ].includes(processedNotification.state as NotificationProcessedStateEnum)
}

const getErrorReason = (
  processedNotification: NotificationQueueProcessedModel
) => {
  if (processedNotification.state === NotificationProcessedStateEnum.FAILED) {
    return (
      <>
        <Trans>
          Email/message was rejected by recipients mail server. Reference code:
        </Trans>
        <br /> PROC-NOTIF-{processedNotification.id}
      </>
    )
  }
  return (
    <>
      <Trans>
        Fail reason: Email/message did not reach its destination, due to errors
        on external (non Looop) systems. Please ask our support to provide more
        info, use this reference code while talking to our support
      </Trans>
      : <br />
      PROC-NOTIF-{processedNotification.id}
    </>
  )
}

const renderImmediateActionLabel = (
  scheduledAction: IScheduledAction
): JSX.Element => {
  let icon: ReactNode = null

  if (scheduledAction.email) {
    if (scheduledAction.email.is_for_manager) {
      icon = <IconPeople />
    } else {
      icon = <IconEnvelope />
    }
  }

  if (scheduledAction.banner) {
    icon = <IconScreenDesktop />
  }

  if (scheduledAction.topicShareAction) {
    icon = <IconDoc />
  }

  if (scheduledAction.pathwayShareAction) {
    icon = <IconMap />
  }

  if (scheduledAction.surveyShareAction) {
    icon = <IconBubbles />
  }

  if (scheduledAction.message) {
    icon = <IconBubble />
  }

  if (scheduledAction.certificationShareAction) {
    icon = <IconBadge />
  }

  return (
    <div className="EventHeader__label EventHeader__label--relative">
      {icon}
    </div>
  )
}

export default CampaignActionReportOverviewTable
